import { useState, useCallback, useEffect } from "react";
import { toast } from "@/lib/toast";
import { captureEvent } from "@/lib/analyticsQueue";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import {
  cancelSubscription,
  applyDiscount,
  getSubscriptionInfo,
} from "@/lib/api/payments";
import { capturePaymentError } from "@/lib/sentry";
import { ANALYTICS_EVENTS } from "@shared/schemas/analytics";
import type { SubscriptionInfo } from "@/lib/api/payments";
import { SKIP_OFFER_REASONS } from "./constants";
import type { CancellationStep, CancellationReason } from "./types";

export function useCancelSubscription(onComplete: () => void) {
  const { t } = useLanguage();
  const { profile } = useAuth();
  const hasExistingDiscount = (profile?.discount_charges_remaining ?? 0) > 0;
  const [step, setStep] = useState<CancellationStep>("reason");
  const [selectedReason, setSelectedReason] =
    useState<CancellationReason | null>(null);
  const [feedback, setFeedback] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [subscriptionInfo, setSubscriptionInfo] =
    useState<SubscriptionInfo | null>(null);

  useEffect(() => {
    captureEvent(ANALYTICS_EVENTS.CANCELLATION_STARTED, {
      has_existing_discount: hasExistingDiscount,
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const reset = useCallback(() => {
    setStep("reason");
    setSelectedReason(null);
    setFeedback("");
    setIsLoading(false);
    setSubscriptionInfo(null);
  }, []);

  // Fetch subscription info when reaching confirmation step
  useEffect(() => {
    if (step !== "confirm") return;
    let cancelled = false;

    getSubscriptionInfo()
      .then((info) => {
        if (!cancelled) setSubscriptionInfo(info);
      })
      .catch(() => {
        // Non-critical — dialog still works without loss summary data
      });

    return () => {
      cancelled = true;
    };
  }, [step]);

  const handleReasonContinue = useCallback(() => {
    if (!selectedReason) return;

    // Skip offer step only if reason is in skip list (e.g. "other")
    if (SKIP_OFFER_REASONS.includes(selectedReason)) {
      setStep("confirm");
      captureEvent(ANALYTICS_EVENTS.CANCELLATION_REASON_SELECTED, {
        reason: selectedReason,
        has_feedback: !!feedback,
        offered_discount: false,
      });
    } else {
      setStep("offer");
      captureEvent(ANALYTICS_EVENTS.CANCELLATION_REASON_SELECTED, {
        reason: selectedReason,
        has_feedback: !!feedback,
        offered_discount: true,
      });
      captureEvent(ANALYTICS_EVENTS.CANCELLATION_DISCOUNT_OFFERED, {
        reason: selectedReason,
        has_existing_discount: hasExistingDiscount,
      });
    }
  }, [selectedReason, feedback, hasExistingDiscount]);

  const handleAcceptDiscount = useCallback(async () => {
    if (!selectedReason) return;
    setIsLoading(true);
    captureEvent(ANALYTICS_EVENTS.CANCELLATION_DISCOUNT_ACCEPTED, {
      reason: selectedReason,
    });
    try {
      await applyDiscount({
        reason: selectedReason,
        feedback: feedback || undefined,
      });
      toast.success(t.cancellation.discountApplied);
      onComplete();
      reset();
    } catch (error) {
      capturePaymentError(error, { endpoint: "/api/payments/apply-discount", action: "apply_discount" });
      captureEvent(ANALYTICS_EVENTS.CANCELLATION_ERROR, {
        action: "accept_discount",
      });
      toast.error(t.cancellation.discountError);
    } finally {
      setIsLoading(false);
    }
  }, [selectedReason, feedback, t, onComplete, reset]);

  const handleDeclineOffer = useCallback(() => {
    captureEvent(ANALYTICS_EVENTS.CANCELLATION_DISCOUNT_DECLINED, {
      reason: selectedReason,
    });
    setStep("confirm");
  }, [selectedReason]);

  const handleConfirmCancel = useCallback(async () => {
    if (!selectedReason) return;
    setIsLoading(true);
    captureEvent(ANALYTICS_EVENTS.CANCELLATION_CONFIRMED, {
      reason: selectedReason,
      has_feedback: !!feedback,
    });
    try {
      await cancelSubscription({
        reason: selectedReason,
        feedback: feedback || undefined,
      });
      toast.success(t.cancellation.subscriptionCancelled);
      onComplete();
      reset();
    } catch (error) {
      capturePaymentError(error, { endpoint: "/api/payments/cancel-subscription", action: "cancel_subscription" });
      captureEvent(ANALYTICS_EVENTS.CANCELLATION_ERROR, {
        action: "confirm_cancel",
      });
      toast.error(t.cancellation.cancelError);
    } finally {
      setIsLoading(false);
    }
  }, [selectedReason, feedback, t, onComplete, reset]);

  return {
    step,
    selectedReason,
    setSelectedReason,
    feedback,
    setFeedback,
    isLoading,
    subscriptionInfo,
    hasExistingDiscount,
    handleReasonContinue,
    handleAcceptDiscount,
    handleDeclineOffer,
    handleConfirmCancel,
    reset,
  };
}
